import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  BatchDeleteReferralDto,
  BatchUpdateReferralStatusDto,
  CreateReferralDto,
  ReferralItem,
  ReferralPageResult,
  ReferralQueryParams,
  UpdateReferralDto,
} from './types/referral';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/referrals';

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const normalizeReferralPage = (resp: unknown): ReferralPageResult => {
  if (!isObject(resp)) {
    throw new Error('介绍关系列表返回结构异常，请检查 API 地址或代理配置');
  }

  const root = resp as Record<string, unknown>;
  const candidates: unknown[] = [
    root,
    root.data,
    root.result,
    root.payload,
    (root.response as Record<string, unknown> | undefined)?.data,
  ];

  for (const candidate of candidates) {
    if (!isObject(candidate)) {
      continue;
    }
    const pageObj = candidate as Record<string, unknown>;
    const hasList = Array.isArray(pageObj.list);
    const hasItems = Array.isArray(pageObj.items);
    const hasDataArray = Array.isArray(pageObj.data);
    const data: unknown[] = hasList
      ? (pageObj.list as unknown[])
      : hasItems
        ? (pageObj.items as unknown[])
        : hasDataArray
          ? (pageObj.data as unknown[])
          : [];
    const totalRaw = pageObj.total ?? pageObj.count ?? data.length;
    const pageRaw = pageObj.page ?? pageObj.current ?? 1;
    const pageSizeRaw = (pageObj.pageSize ?? pageObj.limit ?? data.length) || 20;

    if (
      hasList ||
      hasItems ||
      hasDataArray ||
      pageObj.total !== undefined ||
      pageObj.count !== undefined
    ) {
      return {
        list: data.map((item) => unwrapReferralDetail(item)),
        total: Number(totalRaw) || 0,
        page: Number(pageRaw) || 1,
        pageSize: Number(pageSizeRaw) || 20,
      };
    }
  }

  throw new Error('介绍关系列表返回结构异常，请检查 API 地址或代理配置');
};

const unwrapReferralDetail = (resp: unknown): ReferralItem => {
  if (resp && typeof resp === 'object') {
    const obj = resp as Record<string, unknown>;
    const candidates = [
      obj,
      obj.data,
      obj.result,
      obj.payload,
      (obj.response as Record<string, unknown> | undefined)?.data,
    ];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') continue;
      const record = candidate as Record<string, unknown>;
      if (
        typeof record.uuid === 'string' &&
        typeof record.referrerUuid === 'string' &&
        typeof record.targetUuid === 'string'
      ) {
        return candidate as ReferralItem;
      }
    }
  }
  throw new Error('介绍关系详情返回结构异常');
};

export async function queryReferrals(
  params: ReferralQueryParams,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(BASE_PATH, {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return normalizeReferralPage(resp);
}

export async function queryReferralDetail(uuid: string, options?: ApiRequestOptions) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
  return unwrapReferralDetail(resp);
}

export async function createReferral(payload: CreateReferralDto, options?: ApiRequestOptions) {
  const resp = await request<unknown>(BASE_PATH, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
  return unwrapReferralDetail(resp);
}

export async function updateReferral(
  uuid: string,
  payload: UpdateReferralDto,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
  return unwrapReferralDetail(resp);
}

export async function deleteReferral(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function batchDeleteReferrals(
  payload: BatchDeleteReferralDto,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/batch-delete`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function batchUpdateReferralStatus(
  payload: BatchUpdateReferralStatusDto,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/batch-status`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function restoreReferral(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/${uuid}/restore`, {
    method: 'PATCH',
    ...(options || {}),
  });
}
